import React, { useCallback } from 'react';
import { BaseEdge, EdgeLabelRenderer, getSimpleBezierPath, EdgeProps, Position } from '@xyflow/react';
import { colors, text, ui } from '@annote/core/styles';
import { UUID } from '@annote/types';
import styled from 'styled-components';
import { ReactComponent as AcceptIcon } from 'src/components/Icon/icons/accept.svg';
import { ReactComponent as RejectIcon } from 'src/components/Icon/icons/reject.svg';
import { useSuggestedContext } from 'src/context/SuggestedContext';

const SuggestionButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const SuggestionButtonsPositioner = styled.div<{ labelX: number; labelY: number }>`
  position: absolute;
  transform: translate(-50%, -50%) translate(${({ labelX }) => labelX}px, ${({ labelY }) => labelY}px);
`;

const SuggestionButton = styled.button<{ variant: 'accept' | 'reject' }>`
  border: none;
  border-radius: 4px;
  padding: 0.75rem;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0px 3px 6px rgba(15, 15, 15, 0.1);
  line-height: ${text.height.tight};
  font-size: ${text.size.small};
  font-family: ${text.family.primary};
  font-weight: 500;
  cursor: pointer;
  z-index: 1011;
  pointer-events: all;

  ${({ variant }) =>
    variant === 'accept'
      ? `
    background-color: ${colors.button.accept.primary};
    color: white;
    
    &:hover {
      background-color: ${colors.button.accept.hover};
      box-shadow: 0px 9px 24px rgba(15, 15, 15, 0.2), 0px 3px 6px rgba(15, 15, 15, 0.1);
    }
  `
      : `
    background-color: ${colors.button.reject.primary};
    color: white;
    
    &:hover {
      background-color: ${colors.button.reject.hover};
      box-shadow: 0px 9px 24px rgba(15, 15, 15, 0.2), 0px 3px 6px rgba(15, 15, 15, 0.1);
    }
  `}
`;

interface SuggestedEdgeProps extends EdgeProps {
  id: UUID;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  selected?: boolean;
  data?: {
    reason?: string;
    score?: number;
  };
}

const SuggestedEdge: React.FC<SuggestedEdgeProps> = ({
  id, // This is the suggestionId!
  sourceX,
  sourceY,
  targetX,
  targetY,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selected,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  data,
  ...props
}) => {
  const { acceptSuggestion, rejectSuggestion } = useSuggestedContext();

  const [edgePath, labelX, labelY] = getSimpleBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition: Position.Left,
    targetPosition: Position.Right,
  });

  const onAcceptClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      acceptSuggestion(id);
    },
    [id, acceptSuggestion],
  );

  const onRejectClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      rejectSuggestion(id);
    },
    [id, rejectSuggestion],
  );

  // Filter out non-DOM props to prevent React warnings
  const {
    sourcePosition: _sourcePosition,
    targetPosition: _targetPosition,
    sourceHandleId: _sourceHandleId,
    targetHandleId: _targetHandleId,
    pathOptions: _pathOptions,
    selectable: _selectable,
    deletable: _deletable,
    ...domProps
  } = props;

  return (
    <>
      <BaseEdge
        path={edgePath}
        {...domProps}
        style={{
          stroke: '#FFA500', // Orange color for suggestions
          strokeWidth: 2,
          strokeDasharray: '5,5', // Dashed line to indicate it's a suggestion
        }}
      />
      <EdgeLabelRenderer>
        <SuggestionButtonsPositioner labelX={labelX} labelY={labelY}>
          <SuggestionButtonsWrapper className="nodrag nopan">
            <SuggestionButton variant="reject" onClick={onRejectClick}>
              <RejectIcon width={ui.getIconSizePx('cardButton')} height={ui.getIconSizePx('cardButton')} />
            </SuggestionButton>
            <SuggestionButton variant="accept" onClick={onAcceptClick}>
              <AcceptIcon width={ui.getIconSizePx('cardButton')} height={ui.getIconSizePx('cardButton')} />
            </SuggestionButton>
          </SuggestionButtonsWrapper>
        </SuggestionButtonsPositioner>
      </EdgeLabelRenderer>
    </>
  );
};

export default SuggestedEdge;
